import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Container,
  IconButton,
  Paper,
  Stack,
  CircularProgress,
  TextField,
  InputAdornment,
  Zoom,
} from '@mui/material';
import {
  LocalHospital as Hospital,
  MedicalServices as Stethoscope,
  Science as FlaskConical,
  Business as Building2,
  LocalPharmacy as Pill,
  People as Users,
  ChevronRight,
  Search as SearchIcon,
  Clear as ClearIcon,
  Dashboard as DashboardIcon,
  TrendingUp,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';

import { getAllDepartments, categorie } from '../data/department';
import { getAcc } from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import ConsumableRequisitionDialog from '../Procurement/ConsumableRequisitionDialog';

const Dashboard = () => {
  const [showAdminPanel, setShowAdminPanel] = React.useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [getAccInfo, setgetAccInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [departments, setdepartments] = useState(getAllDepartments);
  const [categories, setCategories] = useState(categorie);

  // "In-charge" users (staff.is_staff = 1) get a Request Consumables CTA
  // on their dashboard. Click opens a self-contained dialog that posts a
  // 'consumable' requisition into the same Procurement inbox drugs use.
  const [consumablesDialogOpen, setConsumablesDialogOpen] = useState(false);

  const navigate = useNavigate();

  // Custom color scheme
  const PRIMARY_COLOR = '#1A9B7F';
  const PRIMARY_DARK = '#157763';
  const PRIMARY_LIGHT = '#20B894';

  useEffect(() => {
    try {
      const savedUserInfo = localStorage.getItem('userInfo');
      if (savedUserInfo) {
        const parsed = JSON.parse(savedUserInfo);
        if (parsed && parsed.name && parsed.role) {
          setUserInfo(parsed);
        } else {
          throw new Error('Invalid user info structure');
        }
      } else {
        throw new Error('No user info found');
      }
    } catch (error) {
      console.error('Failed to load user info:', error);
      setError('Failed to load user information');
    }
  }, []);

  useEffect(() => {
    if (userInfo) {
      loadRoles();
    }
  }, [userInfo]);

  const loadRoles = async () => {
    setLoading(true);
    setError(null);

    try {
      const role = userInfo.role;
      const response = await axios.post(getAcc, { role });
      if (response.data.success && Array.isArray(response.data.results)) {
        setgetAccInfo(response.data.results);
        console.log(response.data.results);
      } else {
        throw new Error('Failed to load roles');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load roles';
      setError(errorMessage);

      await Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const accessibleDepartmentIds = useMemo(() => {
    if (userInfo?.role === 'System Administrator') {
      return departments.map(dept => dept.id);
    }

    if (!Array.isArray(getAccInfo)) {
      return [];
    }

    return getAccInfo.map(item => item.department).filter(Boolean);
  }, [getAccInfo, userInfo?.role, departments]);

  const accessibleDepartments = useMemo(() => {
    if (userInfo?.role === 'System Administrator') {
      return departments;
    }

    if (!accessibleDepartmentIds.length) {
      return [];
    }

    return accessibleDepartmentIds
      .map(deptId => departments.find(d => d.id === deptId))
      .filter(Boolean);
  }, [accessibleDepartmentIds, departments, userInfo?.role]);

  const filteredDepartments = useMemo(() => {
    if (!searchQuery.trim()) {
      return accessibleDepartments;
    }

    return accessibleDepartments.filter(dept =>
      dept.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dept.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [accessibleDepartments, searchQuery]);

  const groupedDepartments = useMemo(() => {
    const grouped = {};

    if (!filteredDepartments.length) {
      return grouped;
    }

    Object.entries(categories).forEach(([categoryName, deptIds]) => {
      const categoryDepts = filteredDepartments.filter(dept =>
        deptIds.includes(dept.id)
      );
      if (categoryDepts.length > 0) {
        grouped[categoryName] = categoryDepts;
      }
    });

    const uncategorizedDepts = filteredDepartments.filter(dept =>
      !Object.values(categories).flat().includes(dept.id)
    );
    if (uncategorizedDepts.length > 0) {
      grouped['Other Services'] = uncategorizedDepts;
    }

    return grouped;
  }, [filteredDepartments]);

  const getCategoryIcon = (category) => {
    if (category.includes('Clinical')) return <Stethoscope />;
    if (category.includes('Diagnostic')) return <FlaskConical />;
    if (category.includes('Administrative')) return <Building2 />;
    if (category.includes('Specialty')) return <Pill />;
    return <Stethoscope />;
  };

  const getCategoryColor = (category) => {
    if (category.includes('Clinical')) return { main: PRIMARY_COLOR, light: PRIMARY_LIGHT };
    if (category.includes('Diagnostic')) return { main: '#2196F3', light: '#42A5F5' };
    if (category.includes('Administrative')) return { main: '#FF9800', light: '#FFB74D' };
    if (category.includes('Specialty')) return { main: '#9C27B0', light: '#BA68C8' };
    return { main: PRIMARY_COLOR, light: PRIMARY_LIGHT };
  };

  if (loading || !userInfo) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e3e9f0 100%)',
      }}>
        <style>
          {`
            @keyframes pulse-ring {
              0% {
                transform: scale(0.95);
                opacity: 1;
              }
              50% {
                transform: scale(1.05);
                opacity: 0.7;
              }
              100% {
                transform: scale(0.95);
                opacity: 1;
              }
            }
          `}
        </style>
        <Stack alignItems="center" spacing={3}>
          <Box sx={{ position: 'relative' }}>
            <CircularProgress 
              size={60} 
              thickness={4}
              sx={{ 
                color: PRIMARY_COLOR,
                animation: 'pulse-ring 1.5s ease-in-out infinite'
              }} 
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <Hospital sx={{ fontSize: 32, color: PRIMARY_COLOR }} />
            </Box>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.700' }}>
            Loading Dashboard...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we prepare your workspace
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e3e9f0 100%)',
      }}>
        <Zoom in={true}>
          <Paper sx={{ 
            p: 5, 
            textAlign: 'center', 
            maxWidth: 450,
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                boxShadow: '0 4px 20px rgba(255,107,107,0.3)'
              }}
            >
              <ClearIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'grey.900' }}>
              Oops! Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {error}
            </Typography>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => window.location.reload()}
              sx={{
                background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${PRIMARY_DARK} 100%)`,
                borderRadius: 3,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: `0 4px 14px rgba(26, 155, 127, 0.3)`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${PRIMARY_LIGHT} 0%, ${PRIMARY_COLOR} 100%)`,
                  boxShadow: `0 6px 20px rgba(26, 155, 127, 0.4)`,
                }
              }}
            >
              Retry
            </Button>
          </Paper>
        </Zoom>
      </Box>
    );
  }

  const onDepartmentSelect = (department) => {
    console.log(department);

    if (department.name === 'Medical records') {
      navigate('/dashboard/Record');
    }

    if (department.name === 'Pharmacy') {
      navigate('/dashboard/Pharmacy');
    }

    if (department.name === 'Billing & Accounts') {
      navigate('/dashboard/Accounts');
    }

    if (department.name === 'General Medical Services') {
      navigate('/dashboard/Doctors');
    }

    if (department.name === 'Wards & Nursing Services') {
      navigate('/dashboard/Wards');
    }

    if (department.name === 'Procurement & Supplies') {
      navigate('/dashboard/Procurement');
    }

    if (department.name === 'Investigations') {
      navigate('/dashboard/Laboratory');
    }

    if (department.name === 'Hospital Claims') {
      navigate('/dashboard/Claims');
    }

    if (department.name === 'Health Information') {
      navigate('/dashboard/HealthInformation');
    }

    if (department.name === 'Health Information') {
      navigate('/dashboard/HealthInformation');
    }

    if (department.name === 'Emergency (ER/A&E)') {
      navigate('/dashboard/Emergency');
    }

    if (department.name === 'Human Resources (HR)') {
      navigate('/dashboard/HumanResources');
    }

  };

  return (
    <Box sx={{
      minHeight: '100vh',
      position: 'relative',
      background: 'linear-gradient(180deg, #f6fbf9 0%, #eef4f3 100%)',
      fontFamily: '"Inter","Segoe UI","Helvetica Neue",Arial,sans-serif',
      overflow: 'hidden',
    }}>
      <style>
        {`
          @keyframes blobDrift1 {
            0%,100% { transform: translate(0,0) scale(1); }
            33%     { transform: translate(40px,-30px) scale(1.05); }
            66%     { transform: translate(-30px,30px) scale(0.95); }
          }
          @keyframes blobDrift2 {
            0%,100% { transform: translate(0,0) scale(1); }
            50%     { transform: translate(-50px,-40px) scale(1.08); }
          }
          @keyframes blobDrift3 {
            0%,100% { transform: translate(0,0) scale(1); }
            50%     { transform: translate(35px,40px) scale(0.92); }
          }
          @keyframes sparkRise {
            0%   { transform: translateY(0) scale(0.6); opacity: 0; }
            10%  { opacity: 0.6; }
            50%  { opacity: 1; transform: translateY(-40vh) scale(1); }
            90%  { opacity: 0.4; }
            100% { transform: translateY(-90vh) scale(0.6); opacity: 0; }
          }
          @keyframes pulseDot {
            0%,100% { box-shadow: 0 0 0 0 rgba(74,222,128,0.55); }
            50%     { box-shadow: 0 0 0 8px rgba(74,222,128,0); }
          }
          .bg-blob {
            position: absolute;
            border-radius: 50%;
            filter: blur(70px);
            pointer-events: none;
          }
          .bg-grid {
            position: absolute;
            inset: 0;
            background-image:
              linear-gradient(rgba(15,23,42,0.025) 1px, transparent 1px),
              linear-gradient(90deg, rgba(15,23,42,0.025) 1px, transparent 1px);
            background-size: 56px 56px;
            mask-image: linear-gradient(180deg, rgba(0,0,0,0.7), transparent 80%);
            -webkit-mask-image: linear-gradient(180deg, rgba(0,0,0,0.7), transparent 80%);
            pointer-events: none;
          }
          .bg-cross {
            position: absolute;
            right: -60px;
            bottom: -40px;
            width: 320px;
            opacity: 0.04;
            pointer-events: none;
          }
          .spark {
            position: absolute;
            bottom: -10px;
            width: 4px;
            height: 4px;
            border-radius: 50%;
            background: ${PRIMARY_COLOR};
            box-shadow: 0 0 8px ${PRIMARY_COLOR}, 0 0 14px ${PRIMARY_COLOR}88;
            pointer-events: none;
            animation: sparkRise linear infinite;
          }

          .department-card {
            transition: border-color .18s ease, box-shadow .22s ease, transform .18s ease;
          }
          .department-card:hover {
            border-color: ${PRIMARY_COLOR};
            box-shadow: 0 10px 28px rgba(15,23,42,0.10);
            transform: translateY(-3px);
          }
          .department-card:hover .dept-arrow {
            background: ${PRIMARY_COLOR};
            color: #fff;
            transform: translateX(2px);
          }
          .stat-tile {
            transition: box-shadow .18s ease, transform .18s ease;
            backdrop-filter: blur(6px);
          }
          .stat-tile:hover {
            box-shadow: 0 8px 22px rgba(15,23,42,0.08);
            transform: translateY(-1px);
          }
          .hero-card {
            position: relative;
            overflow: hidden;
            backdrop-filter: blur(10px);
          }
          .hero-card::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -20%;
            width: 360px;
            height: 360px;
            border-radius: 50%;
            background: radial-gradient(circle, ${PRIMARY_LIGHT}33 0%, transparent 65%);
            pointer-events: none;
          }
        `}
      </style>

      {/* ── Animated background layers ───────────────────────────────── */}
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
        <Box className="bg-blob" sx={{
          width: 520, height: 520, top: '-180px', left: '-160px',
          background: `radial-gradient(circle, ${PRIMARY_COLOR}55 0%, transparent 65%)`,
          animation: 'blobDrift1 22s ease-in-out infinite',
        }} />
        <Box className="bg-blob" sx={{
          width: 460, height: 460, top: '20%', right: '-180px',
          background: 'radial-gradient(circle, rgba(37,99,235,0.30) 0%, transparent 65%)',
          animation: 'blobDrift2 28s ease-in-out infinite',
        }} />
        <Box className="bg-blob" sx={{
          width: 420, height: 420, bottom: '-160px', left: '30%',
          background: 'radial-gradient(circle, rgba(124,58,237,0.22) 0%, transparent 65%)',
          animation: 'blobDrift3 26s ease-in-out infinite',
        }} />
        <Box className="bg-grid" />
        <svg className="bg-cross" viewBox="0 0 100 100">
          <path fill={PRIMARY_DARK} d="M40 0h20v40h40v20H60v40H40V60H0V40h40z" />
        </svg>

        {/* drifting sparks */}
        {[...Array(14)].map((_, i) => {
          const left = (i * 7.3 + 5) % 100;
          const dur = 14 + (i % 5) * 3;
          const delay = (i * 1.1) % 12;
          const size = 3 + (i % 3);
          return (
            <span
              key={i}
              className="spark"
              style={{
                left: `${left}%`,
                width: size,
                height: size,
                animationDuration: `${dur}s`,
                animationDelay: `-${delay}s`,
                background: i % 3 === 0 ? '#2563eb' : i % 3 === 1 ? PRIMARY_COLOR : '#7c3aed',
                boxShadow: `0 0 8px currentColor`,
              }}
            />
          );
        })}
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>

        {/* ── Hero Welcome Card ─────────────────────────────────────────── */}
        <Paper
          elevation={0}
          className="hero-card"
          sx={{
            mb: 3.5,
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 3,
            border: '1px solid rgba(15,23,42,0.06)',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(246,251,249,0.85) 100%)',
            boxShadow: '0 4px 20px rgba(15,23,42,0.04)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: '#4ade80',
              animation: 'pulseDot 2.4s ease-in-out infinite',
            }} />
            <Typography sx={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: PRIMARY_COLOR,
              letterSpacing: '1.6px',
              textTransform: 'uppercase',
            }}>
              Dashboard &middot; Live
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: { xs: '1.7rem', md: '2.1rem' },
              fontWeight: 800,
              color: '#0f172a',
              letterSpacing: '-0.6px',
              lineHeight: 1.15,
              mb: 0.75,
              position: 'relative',
            }}
          >
            Welcome back, {userInfo.name?.split(' ')[0] || 'User'}
          </Typography>
          <Typography sx={{ color: '#475569', fontSize: { xs: '0.92rem', md: '1rem' }, position: 'relative', maxWidth: 620 }}>
            {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} &middot; Select a department below to begin your work.
          </Typography>

          {/* Quick action — Request Consumables. Only shown to users with
              the in-charge flag (staff.is_staff = 1). Drug requests live
              inside Pharmacy → Requisition; this CTA is the consumables
              equivalent, routed through Procurement → Manage Requisitions
              with item_type='consumable'. */}
          {userInfo.is_staff === 1 && (
            <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                disableElevation
                startIcon={<ShoppingCartIcon sx={{ fontSize: 18 }} />}
                onClick={() => setConsumablesDialogOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 2.25, py: 0.9,
                  bgcolor: PRIMARY_COLOR,
                  '&:hover': { bgcolor: PRIMARY_DARK },
                  boxShadow: '0 2px 8px rgba(26,155,127,0.25)',
                }}>
                Request Consumables
              </Button>
              <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>
                In-charge access — send a request to Procurement for cotton, plaster, cannulas, etc.
              </Typography>
            </Box>
          )}
        </Paper>

        {/* ── Quick Stats ─────────────────────────────────────────────── */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          {[
            {
              label: 'Departments',
              value: accessibleDepartments.length,
              icon: <Building2 sx={{ fontSize: 22, color: PRIMARY_COLOR }} />,
              accent: PRIMARY_COLOR,
            },
            {
              label: 'Your Role',
              value: userInfo.role || '—',
              icon: <Users sx={{ fontSize: 22, color: '#2563eb' }} />,
              accent: '#2563eb',
              isText: true,
            },
            {
              label: 'Categories',
              value: Object.keys(groupedDepartments).length,
              icon: <DashboardIcon sx={{ fontSize: 22, color: '#7c3aed' }} />,
              accent: '#7c3aed',
            },
            {
              label: 'Status',
              value: 'Online',
              icon: <TrendingUp sx={{ fontSize: 22, color: '#16a34a' }} />,
              accent: '#16a34a',
              isText: true,
            },
          ].map((s) => (
            <Grid item xs={6} md={3} key={s.label}>
              <Paper
                className="stat-tile"
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  border: '1px solid rgba(15,23,42,0.06)',
                  bgcolor: 'rgba(255,255,255,0.85)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.75,
                }}
              >
                <Box sx={{
                  width: 42, height: 42, borderRadius: 2,
                  bgcolor: `${s.accent}12`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {s.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', lineHeight: 1.3 }}>
                    {s.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: s.isText ? '0.95rem' : '1.4rem',
                      fontWeight: 700,
                      color: '#0f172a',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {s.value}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ── Search ──────────────────────────────────────────────────── */}
        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search departments…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              maxWidth: 560,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(6px)',
                fontSize: '0.95rem',
                '& fieldset': { borderColor: 'rgba(15,23,42,0.08)' },
                '&:hover fieldset': { borderColor: PRIMARY_COLOR },
                '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR, borderWidth: 1.5 },
                '&.Mui-focused': { boxShadow: `0 0 0 4px rgba(26,155,127,0.10)` },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{ color: '#94a3b8', '&:hover': { color: '#475569' } }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {searchQuery && (
            <Typography sx={{ color: '#64748b', mt: 1.25, fontSize: '0.85rem' }}>
              Found <strong style={{ color: PRIMARY_COLOR }}>{filteredDepartments.length}</strong> department{filteredDepartments.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        {/* ── Departments by Category ─────────────────────────────────── */}
        <Box>
          {Object.entries(groupedDepartments).map(([category, categoryDepartments]) => {
            const accent = getCategoryColor(category).main;
            return (
              <Box key={category} sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 36, height: 36, borderRadius: 1.75,
                      bgcolor: `${accent}14`, color: accent,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {React.cloneElement(getCategoryIcon(category), { sx: { fontSize: 20 } })}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a', lineHeight: 1.25 }}>
                      {category}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                      {categoryDepartments.length} {categoryDepartments.length === 1 ? 'department' : 'departments'}
                    </Typography>
                  </Box>
                </Stack>

                <Grid container spacing={2}>
                  {categoryDepartments.map((department) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={department.id}>
                      <Card
                        component="button"
                        onClick={() => onDepartmentSelect(department)}
                        className="department-card"
                        elevation={0}
                        sx={{
                          width: '100%',
                          textAlign: 'left',
                          cursor: 'pointer',
                          border: '1px solid rgba(15,23,42,0.06)',
                          borderRadius: 2.5,
                          bgcolor: 'rgba(255,255,255,0.92)',
                          backdropFilter: 'blur(6px)',
                          p: 0,
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ height: 3, bgcolor: accent }} />
                        <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.25 }}>
                            <Box
                              sx={{
                                width: 36, height: 36, borderRadius: 1.5,
                                bgcolor: `${accent}14`, color: accent,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}
                            >
                              {React.cloneElement(getCategoryIcon(category), { sx: { fontSize: 19 } })}
                            </Box>
                            <Box
                              className="dept-arrow"
                              sx={{
                                width: 28, height: 28, borderRadius: 1.25,
                                bgcolor: '#f1f5f9', color: '#64748b',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all .18s ease',
                              }}
                            >
                              <ChevronRight sx={{ fontSize: 18 }} />
                            </Box>
                          </Box>

                          <Typography
                            sx={{
                              fontWeight: 700, fontSize: '0.98rem',
                              color: '#0f172a', mb: 0.5, lineHeight: 1.3,
                            }}
                          >
                            {department.name}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: '0.83rem', color: '#64748b',
                              lineHeight: 1.55,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              minHeight: '2.6em',
                            }}
                          >
                            {department.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })}
        </Box>

        {/* Empty State */}
        {Object.keys(groupedDepartments).length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                textAlign: 'center',
                maxWidth: 460,
                borderRadius: 2.5,
                border: '1px solid #e5e9ef',
                bgcolor: '#fff',
              }}
            >
              <Box
                sx={{
                  width: 64, height: 64, borderRadius: 2,
                  bgcolor: `${PRIMARY_COLOR}12`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 1.25rem',
                }}
              >
                <Users sx={{ fontSize: 30, color: PRIMARY_COLOR }} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: '#0f172a', mb: 1, fontSize: '1.1rem' }}>
                {searchQuery ? 'No matching departments' : 'No departments available'}
              </Typography>
              <Typography sx={{ color: '#64748b', mb: 2.5, fontSize: '0.9rem', lineHeight: 1.6 }}>
                {searchQuery
                  ? `Nothing matched "${searchQuery}". Try a different search term.`
                  : 'No departments are currently available for your access level. Please contact your administrator.'}
              </Typography>
              {searchQuery && (
                <Button
                  variant="outlined"
                  onClick={() => setSearchQuery('')}
                  sx={{
                    borderColor: PRIMARY_COLOR,
                    color: PRIMARY_COLOR,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': { borderColor: PRIMARY_DARK, bgcolor: `${PRIMARY_COLOR}08` },
                  }}
                >
                  Clear Search
                </Button>
              )}
            </Paper>
          </Box>
        )}
      </Container>

      {/* Consumables request dialog — mounted at the root so it overlays
          the whole dashboard, not just the hero card. */}
      <ConsumableRequisitionDialog
        open={consumablesDialogOpen}
        onClose={() => setConsumablesDialogOpen(false)}
      />
    </Box>
  );
};

export default Dashboard;